import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMerchant } from "@/hooks/useMerchant";
import { useProtectTRPCClient } from "../trpc";
import type { BenchInput } from "./types";

// Prefix of every quote in the cache, to drop them all at once.
export const INDUCTION_QUOTE_KEY = ["protect", "induction", "quote"] as const;

type Input = {
  bench: BenchInput;
  enabled?: boolean;
};

// The server prices one table per bench (10 rows, 10% to 100% of the
// pre-approved list); the steps pick the row for the selection with
// `quoteRowFor`. The key is ours so the wizard can drop every quote at once.
export function useInductionQuote({ bench, enabled = true }: Input) {
  const { merchant } = useMerchant();
  const client = useProtectTRPCClient();
  const rentalsMerchantId = merchant?.id ?? "";

  const query = useQuery({
    queryKey: [...INDUCTION_QUOTE_KEY, rentalsMerchantId, bench],
    queryFn: () => client.induction.quote.query({ rentalsMerchantId, bench }),
    enabled: enabled && !!rentalsMerchantId,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: 1,
  });

  return {
    quote: query.data,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
    // True only when the data is for this bench, not a previous one kept
    // while the new quote loads.
    isCurrent: !!query.data && !query.isPlaceholderData,
  };
}
